import { action, configure, decorate, observable, runInAction } from 'mobx';
import queryString from 'query-string';
import LZString from 'lz-string';
import SignalRMiddleware from 'shared/utils/SignalRHelpers';
import SimulcastLogger, { LOGGER_SETTINGS } from 'shared/utils/SimulcastLogger';
import agent from '../agent';
import { getWsHost } from '../endpoints';
import {
  DEFAULT_LAYOUT_VIEW,
  FULL_LAYOUT_VIEW,
  MINI_LAYOUT_VIEW, ROUTES,
} from '../shared/constants/index';
import { breakpoints } from 'shared/utils/styles';
import {
  addLaneToAuctionInLocalStorage,
  setTokenByAuctionID,
  getTokenByAuctionID,
  addAuctionToLocalStorage, getActiveAuctions
} from '../shared/utils/localStorage';

configure({ enforceActions: 'always' });

export const logIf = (label, message, hash) => {
  if (window.location.hash === `#${hash}`) {
    if (message) {
      console.log(label, message);
      return;
    }
    console.log(label);
  }
};

export const getViewType = () => {
  const vw = Math.max(
    document.documentElement.clientWidth || 0,
    window.innerWidth || 0,
  );
  const vh = Math.max(
    document.documentElement.clientHeight || 0,
    window.innerHeight || 0,
  );

  const dimensions = `${vw}x${vh}`;
  const isTablet = vw >= parseInt(breakpoints.tablet);
  const isSmall = vw >= parseInt(breakpoints.small);
  const isLarge = vw >= parseInt(breakpoints.large);
  const isLarger = vw >= parseInt(breakpoints.larger);
  const isLargest = vw >= parseInt(breakpoints.largest);
  const isMobile = !isTablet && !isSmall && !isLarge && !isLarger && !isLargest;

  logIf(
    'sim1202 - browser size',
    {
      dimensions,
      isMobile,
      isTablet,
      isSmall,
      isLarge,
      isLarger,
      isLargest,
    },
    'debug1202',
  );

  let viewType = 'largest';
  if (isMobile) viewType = 'mobile';
  if (isTablet) viewType = 'tablet';
  if (isSmall) viewType = 'small';
  if (isLarge) viewType = 'large';
  if (isLarger) viewType = 'larger';

  return { viewType, vh, vw, dimensions };
};

class DataStore {
  constructor(MultiAuctionStore, AuthStore, ProviderStore, VirtualAuctionStore) {
    this.multiAuctionStore = MultiAuctionStore;
    this.authStore = AuthStore;
    this.providerStore = ProviderStore;
    this.virtualAuctionStore = VirtualAuctionStore;
    this.connections = new Map();
    this.networkCheckTimeout = null;
    this.reconnectTimeout = null;
    this.initialize();
  }

  initialize = () => {
    // Set up the connection for the specific auction
    //this.handleReconnect();
    console.log('initializing...')
  };

  toggleReconnecting = (state = true) => {
    logIf(
        'sim1202 - toggleReconnecting: ',
        {
          state,
          reconnecting: this.reconnecting,
          connection: this.connection,
        },
        'debug1202',
    );

    if (state === false) {
      runInAction(() => {
        this.reconnecting = false;
      });
      return;
    }

    runInAction(() => {
      if (!this.reconnecting) {
        this.reconnecting = true;
        setTimeout(() => {
          runInAction(() => {
            this.reconnecting = false;
          });
        }, 30000);
      }
    });
  };

  handleReconnect = async () => {
    const savedAuctions = getActiveAuctions(8);
    if (savedAuctions.length < 1) {
      return;
    }
    this.toggleReconnecting();
    runInAction(() => {
      this.reconnecting = false;
    });
    savedAuctions.forEach(({ key, auctionID, auctionInfo }) => {
      if (!this.multiAuctionStore.auctions.has(key)) {
        // addAuction(auctionID, auctionInfo, providerStore);
        // Initialize the WebSocket connection for each auction
        this.addNewConnection({ key, auctionID }).then(res => {
          if (res?.error && !res.success) {
            this.multiAuctionStore.setProperty('connected', false);
            window.location.href = `${ROUTES.login}?state=expired`;
          }
        })
      }
    });
  };

  addNewConnection = async ({key, token = null, auctionID, laneID}) => {
    const lsToken = getTokenByAuctionID(key || auctionID);
    if (this.connections.get(key)) {
      return {
        success: true,
        error: null
      }
    }

    if (token && !lsToken) {
      setTokenByAuctionID(key, token);
    }

    if (!token && !lsToken) {
      return {
        error: "Session expired. Please login again",
        success: false,
      }
    }


    await this.connect({key, auctionID, token: token || lsToken, laneReferenceId: laneID});
  }

  connect = async ({
                     key,
                     auctionID, bidderNumber,
                   name,
                     token,
                   laneReferenceId}) => {

    const host = getWsHost();
    // localStorage.setItem('token', JSON.stringify(token));
    let url;

    if (token) {
      url = `?Name=&Username=&Password=&MemberType=BuyerSeller&BidderToken=${token}&LaneReferenceID=${laneReferenceId}&BidderNumber=1234`;
      if (!laneReferenceId) {
        url = `?Name=&Username=&Password=&MemberType=BuyerSeller&BidderToken=${token}&BidderNumber=1234`;
      }
    } else {
      this.reconnecting = false;
      this.connected = false;
      return;
    }

    const connection = new SignalRMiddleware(host, url).openSocket();

    // Store the connection for this auction
    this.connections.set(key, connection);

    connection.start().then(() => {
      this.setListeners(connection, key);
      const auctionStore = this.multiAuctionStore.addAuction({key});
      auctionStore.setProperty('connected', true);
      if (!this.invalidAuctionError) {
        auctionStore.setProperty('connected', true);

        if (!laneReferenceId) {
            this.sendMessage(key,{
              method: 'JoinAuction',
              payload: [auctionID],
            });

        }
        else {
          this.sendMessage(key, { method: 'JoinLane', payload: [laneReferenceId] });
        }

        if (!this.multiAuctionStore?.pingInterval) {
          this.multiAuctionStore.setProperty('pingInterval', setInterval(() => {
            this.sendMessage(key, {
              method: 'ping',
              payload: [],
            });

            this.networkCheckTimeout = setTimeout(() => {
              this.multiAuctionStore.setProperty('networkConnected', false);

              this.reconnectTimeout = setTimeout(() => {
                runInAction(() => this.reconnecting = true);
              }, 20000)
            }, 3000);
          }, 6000));
        }

        logIf(
            'sim1202 - connect > start()',
            {
              connection: this.connection,
              connectionState: this.connection?.connectionState,
            },
            'debug1202',
        );
        return true;
      }

    })
        .catch(err => {
          console.log(err)
        })
  };

  setListeners(connection, key) {
    const events = [
      'AuctionChanged',
      'AuctionInfo',
      'AuctionList',
      'AuctionMessage',
      'AuctionSettings',
      'BidAmountChanged',
      'BiddingClosed',
      'BiddingOpened',
      'BidderMessage',
      'ConnectionInfo',
      'FinancialCommitments',
      'GlobalPinCleared',
      'GlobalPinSet',
      'LaneBidders',
      'LaneExited',
      'LaneJoined',
      'LaneList',
      'LaneMediaSettings',
      'LaneMessage',
      'LaneMetrics',
      'LanePinCleared',
      'LanePinSet',
      'LaneStatusChangedDetail',
      'MediaStatusChanged',
      'MessageFromClerks',
      'MessageFromClerksToSeller',
      'OnBlockChanged',
      'OnBlockChangedSummary',
      'OnBlockChangedReload',
      'OnBlockHighlight',
      'OnBlockCountdown',
      'pong',
      'ProviderEvents',
      'ProviderSettings',
      'RunList',
      'RunListComp',
      'SellerAction',
      'SystemMessage',
    ];

    events.forEach((eventName) => {
      connection.on(eventName, (...payload) =>
          this.handleMessage(eventName, payload, key)
      );
    });
  }

  sendMessage = async (key, { method, payload = [] }) => {
    // console.log('WS MESSAGE> SEND:', key, method, payload);
    let connection = this.connections.get(key);

    if (!connection) {
      await this.addNewConnection({key});
      connection = this.connections.get(key);
    }

    if (!method) throw new Error('Connection or Method missing');

    try {
      const modPayload = [method, ...payload.map((parm) => parm?.toString())];
      await connection.invoke(...modPayload);
    } catch (error) {
      console.error(`Failed to send message for key: ${key}, method: ${method}`, error);
    }
  };

  async handleMessage(eventName, payload, key) {
    const auctionStore = this.multiAuctionStore.getAuction(key);
    const bidStore = this.multiAuctionStore.getBidStore(key);
    const messageStore = this.multiAuctionStore.getMessageStore(key);

    // if (eventName !== "pong") {
    //   console.log('**** EVENT ****');
    //   console.log(eventName);
    //   console.log(payload);
    //   console.log('*****');
    // }
    let _auctionID;
    switch (eventName) {
      case 'AuctionChanged': {
        const [AuctionID] = payload;
        _auctionID = AuctionID;
        addAuctionToLocalStorage({key: key, auctionID: AuctionID})
        // localStorage.setItem('AuctionID', JSON.stringify(AuctionID));
        const token = getTokenByAuctionID(key);
        // setTokenByAuctionID(AuctionID, token);

        auctionStore.setProperty('actual_auctionID', AuctionID);
        /** Temp fix to resolve the issue of loading saved lanes that aren't associated with the current auction */
        // if (AuctionID !== window.localStorage.getItem('AuctionID')) {
        //   window.localStorage.setItem('AuctionID', AuctionID);
        //   window.localStorage.removeItem('lanes');
        // }

        auctionStore.setProperty('AuctionID', AuctionID);
        break;
      }
      case 'AuctionInfo':
        auctionStore.setAuctionInfo(payload[1]);
        this.multiAuctionStore.setProperty('connected', true);
        break;
      case 'AuctionList': {
        if (!auctionStore.auctionList.length) {
          auctionStore.setProperty('auctionList', payload[0]);
          this.multiAuctionStore.setAllAuctionsAndLanes(payload[0]);
        }
        break;
      }
      case 'AuctionMessage': {
        const messageData = payload[1];
        bidStore.updateNotifications(messageData);
        if (messageData.type === -1) {
          const auctionMessageLogger = new SimulcastLogger({
            component: 'DataStore',
            message: messageData?.message,
            messageType: 'error',
            method: 'emit > AuctionMessage',
            params: {
              localStorage: window.localStorage,
              payload,
              stores: {
                auctionStore: this.auctionStore,
                authStore: this.authStore,
                bidStore: bidStore,
                // dataStore: this.dataStore,
                messageStore: messageStore,
                providerStore: this.providerStore,
              },
            },
            settings: LOGGER_SETTINGS,
            user: bidStore.user,
          });
          auctionMessageLogger.logToConsole();
        }

        break;
      }
      case 'AuctionSettings': {
        const data = payload[1];
        auctionStore.setAuctionSettings(data);
        break;
      }
      case 'BiddingClosed': {
        const LaneID = payload;
        console.log('BiddingClosed > ', LaneID);
        bidStore.BiddingClosed(LaneID);
        break;
      }
      case 'BiddingOpened': {
        const [LaneID, data] = payload;
        bidStore.updateBlock(LaneID, data);
        break;
      }
      case 'BidderMessage': {
        const {type, message} = payload[0];
        this.virtualAuctionStore.setProperty('bidStatusCode', type);
        this.virtualAuctionStore.setProperty('bidStatusMessage', message);
        // type
        // 0 - Last bid was outbid
        // 1 - Any previous bid was outbid
        // 2 - current high bidder
        console.log('BidderMessage *****');
        console.log('BidderMessage');
        console.log('BidderMessage: ', JSON.stringify(payload));
        console.log('BidderMessage *****');
        break;
      }
      case 'OnBlockChanged': {
        const [LaneID, blockData] = payload;

        if (!blockData) return;
        bidStore.updateBlock(LaneID, blockData);
        this.virtualAuctionStore.resetBidStatus();
        break;
      }
      case 'OnBlockChangedSummary': {
        const [laneID, productInfo] = payload;
        auctionStore.setLaneStatus(laneID, productInfo);
        break;
      }

      case 'OnBlockChangedReload': {
        const [laneID, productInfo] = payload;

        if (!productInfo.lotNumber) {
          break;
        }

        try {
          await bidStore.loadProduct(key, laneID, productInfo.lotNumber);
        } catch (error) {
          console.error('OnBlockChange', { error, payload });

          /** If we have a bad session, refresh the connection and attempt to renew */
          if (
              error.response.status === 400 ||
              error.response.status === 401
          ) {
            this.sendMessage(key, {
              method: 'RefreshSession',
              payload: [],
            });
          }
        }

        break;
      }
      case 'ConnectionInfo': {
        const {
          account,
          accountName,
          bidderNumber,
          ipAddress,
          member,
          memberName,
          name,
          providerID,
          queryString: qs,
          sessionToken,
          viewOnly,
        } = payload[0];


        this.multiAuctionStore.setProperty('connected', true);
        this.multiAuctionStore.setUser(
            account,
            accountName,
            member,
            memberName,
            bidderNumber,
            name,
            ipAddress,
            providerID,
            qs,
            viewOnly,
            sessionToken,
        );
        auctionStore.setProperty('bidderNumber', bidderNumber);
        auctionStore.setProperty('api_session_token', sessionToken);
        /** Appends the session token to any outbound REST requests */
        // agent.initInterceptor(sessionToken);
        break;
      }
      case 'FinancialCommitments': {
        const data = payload[1];
        bidStore.setUserCommitments(data);
        break;
      }
      case 'GlobalPinCleared': {
        messageStore.clearGlobalPinnedMessage();
        break;
      }
      case 'GlobalPinSet': {
        const message = payload;
        messageStore.setGlobalPinnedMessage(message);
        break;
      }
      case 'LaneExited': {
        const [LaneID] = payload;
        bidStore.deleteAuctionLane(LaneID);
        messageStore.deleteAuctionLaneMessages(LaneID);
        bidStore.setCloseLane(LaneID);
        break;
      }
      case 'LaneJoined': {
        const { laneID, sellerProducts } = payload[1];

        addLaneToAuctionInLocalStorage({key: key, laneID});

        bidStore.addAuctionLane(laneID);
        bidStore.setAuctionProperty(
            laneID,
            'sellerProducts',
            sellerProducts,
        );

        const viewType = auctionStore.layoutViewType;
        if (
            viewType === DEFAULT_LAYOUT_VIEW ||
            viewType === FULL_LAYOUT_VIEW
        ) {
          bidStore.setAuctionProperty(laneID, 'showRunList', true);
          bidStore.setAuctionProperty(laneID, 'showProdPreview', false);
        } else if (viewType === MINI_LAYOUT_VIEW) {
          bidStore.setAuctionProperty(laneID, 'showRunList', false);
          bidStore.setAuctionProperty(laneID, 'showProdPreview', false);
        }

        bidStore.setLaneRequestInProgress(false);

        runInAction(() => {
          this.reconnecting = false;
        });
        break;
      }
      case 'LaneList': {
        const laneList = payload[1];
        auctionStore.setProperty('laneList', laneList);
        bidStore.setLaneNames();

        const virtualLanes = laneList.filter(lane => lane.isVirtual);
        this.virtualAuctionStore.setProperty('virtualLaneList', virtualLanes);

        // Retrieve stored lanes specific to this auction
        const activeAuctions = getActiveAuctions(8);

        const storedAuction = activeAuctions.find((auction) => auction.key === key);
        const storedLanes = storedAuction ? storedAuction.lanes : [];

        if (storedLanes?.length > 0) {
          // On mobile, join only the first stored lane if not already in any lane
          if (window.innerWidth < 570 && !this?.lanereferenceId) {
            this.sendMessage(key, { method: 'JoinLane', payload: [storedLanes[0]] });
            return;
          }

          // Join each stored lane that is not already open in auctionStore
          storedLanes.forEach((lane) => {
            if (!auctionStore.openLanes.includes(lane)) {
              this.sendMessage(key, { method: 'JoinLane', payload: [lane] });
            }
          });
        }
        break;
      }
      case 'LaneMediaSettings': {
        const [LaneID, data] = payload;
        // TODO: CHECK MEDIA ROOM ID

        auctionStore.setJanusServers(data);

        bidStore.setAuctionProperty(
            LaneID,
            'mediaRoomID',
            data.mediaRoomID,
        );

        bidStore.setAuctionProperty(
            LaneID,
            'audioVideoUrl',
            data.audioVideoUrl,
        );

        bidStore.setAuctionProperty(
            LaneID,
            'mediaToken',
            data.mediaToken,
        );

        break;
      }
      case 'LaneMessage': {
        const [LaneID, data] = payload;

        messageStore.insertLaneActivity(LaneID, data);
        // no sale, sold, or S/T
        if ([5, 6, 7].includes(data.type)) {
          bidStore.setAuctionProperty(LaneID, 'decisionStatus', data);
        }
        bidStore.updateNotifications(data, LaneID);
        if (data.type === -1) {
          messageStore.setLaneErrorMessage(LaneID, {
            message: new Date(),
            type: -1,
          });
          setTimeout(
              () => messageStore.clearLaneErrorMessage(LaneID),
              5000,
          );

          const laneMessageLogger = new SimulcastLogger({
            component: 'DataStore',
            message: data?.message,
            messageType: 'error',
            method: 'emit > LaneMessage',
            params: {
              localStorage: window.localStorage,
              payload,
              stores: {
                auctionStore: auctionStore,
                authStore: this.authStore,
                bidStore: bidStore,
                dataStore: this,
                messageStore: messageStore,
                providerStore: this.providerStore,
              },
            },
            settings: LOGGER_SETTINGS,
            user: bidStore.user,
          });
          laneMessageLogger.logToConsole();
        }

        break;
      }
      case 'LanePinCleared': {
        const [LaneID] = payload;
        messageStore.clearLanePinMessage(LaneID);
        break;
      }
      case 'LanePinSet': {
        const [LaneID, message] = payload;
        messageStore.setLanePinMessage(LaneID, message);
        break;
      }
      case 'LaneStatusChangedDetail': {
        const [LaneID, status] = payload;
        auctionStore.setLaneStatus(LaneID, status);

        const isVirtualLane = this.virtualAuctionStore.virtualLaneList.some(
          (lane) => lane.laneID === LaneID
        );
        if (isVirtualLane) {
          this.virtualAuctionStore.setLanePaused(status.laneStatusName === 'Paused');
          if (status.laneStatusName === 'Running') {
            this.virtualAuctionStore.stopAnnouncement();
          }
        }

        break;
      }
      case 'MediaStatusChanged': {
        const [LaneID, status] = payload;
        bidStore.setAuctionProperty(LaneID, 'mediaStatus', status);
        break;
      }
      case 'MessageFromClerks': {
        const [LaneID, data] = payload;
        messageStore.insertDirectMessageMap(LaneID, data);
        if (
          this.virtualAuctionStore.virtualLaneList.some((lane) => lane.laneID === LaneID) &&
          this.virtualAuctionStore.soundPrefs.newMessage &&
          !messageStore.openChatWindow.includes(LaneID)
        ) {
          this.virtualAuctionStore.playEffect('new_message.mp3');
        }
        break;
      }
      case 'MessageFromClerksToSeller': {
        const [LaneID, data] = payload;
        messageStore.insertDirectMessageMap(LaneID, data);
        if (
          this.virtualAuctionStore.virtualLaneList.some((lane) => lane.laneID === LaneID) &&
          this.virtualAuctionStore.soundPrefs.newMessage &&
          !messageStore.openChatWindow.includes(LaneID)
        ) {
          this.virtualAuctionStore.playEffect('new_message.mp3');
        }
        break;
      }
      case 'OnBlockHighlight' : {
        const [LaneID, data] = payload;
        const {type} = data || {};

        // types
        // 30 = aNewItemOnBlock
        // 31 = sold
        console.log('*******');
        console.log('VA >> on blockHighlight');
        console.log(payload);
        console.log('*******');
        console.log('VA >> TYPE', type)
        switch (type) {
          case 20 : {
            // AnnouncementsStarted — only play for the user's active audio lane, and only if not running
            const activeLaneID = this.virtualAuctionStore.activeLaneID;
            const laneStatus20 = auctionStore.laneStatusMap.get(LaneID);
            console.log('[Announcements] LaneID:', LaneID, '| activeLaneID:', activeLaneID, '| laneStatus:', laneStatus20?.laneStatusName, '| isAudioEnabled:', this.virtualAuctionStore.isAudioEnabled());
            if (LaneID !== activeLaneID) { console.log('[Announcements] blocked: LaneID !== activeLaneID'); break; }
            if (laneStatus20?.laneStatusName === 'Running') { console.log('[Announcements] blocked: lane is Running'); break; }
            this.virtualAuctionStore.playAnnouncement('elane_auction_intro_may-26.mp3');
            break;
          }
          case 21 : {
            // AnnouncementsEnded
            this.virtualAuctionStore.stopAnnouncement();
            break;
          }
          case 30 : {
            // new item

            console.log('VA > NEW ITEM')
            if (this.virtualAuctionStore.soundPrefs.voiceNewItem) {
              this.virtualAuctionStore.playVoiceOver(`on_the_block.mp3`);
            }
            break
          }
          case 31 : {
            // sold
            console.log('VA > SOLD')
            if (!this.virtualAuctionStore.soundPrefs.voiceOutcome) break;
            const {winningBidderNumber} = bidStore.auction[LaneID];
            const {user} = this.multiAuctionStore;

            if (winningBidderNumber === user.bidderNumber) {
              this.virtualAuctionStore.playVoiceOver(`congratulations.mp3`);
              return
            }

            this.virtualAuctionStore.playVoiceOver(`sold.mp3`);
            break
          }
          case 32 : {
            // SubjectTo
            console.log('VA > SubjectTo')
            if (this.virtualAuctionStore.soundPrefs.voiceOutcome) {
              this.virtualAuctionStore.playVoiceOver(`subject_to.mp3`);
            }
            break
          }
          case 33 : {
            // NoSale
            console.log('VA > NoSale')
            if (this.virtualAuctionStore.soundPrefs.voiceOutcome) {
              this.virtualAuctionStore.playVoiceOver(`no_sale.mp3`);
            }
            break
          }
          case 34 : {
            // New bid
            console.log('VA > New bid')
            const {winningBidderNumber} = bidStore.auction[LaneID];
            const {user} = this.multiAuctionStore;

            if (winningBidderNumber && winningBidderNumber !== user.bidderNumber && this.virtualAuctionStore.bidStatusCode !== 0) {
              if (this.virtualAuctionStore.soundPrefs.externalBid) {
                this.virtualAuctionStore.playEffect(`external_bid.mp3`);
              }
            }
            break
          }
        }

        if (type === 30) {
          // New item on the block - start dramatic music

        } else if (type === 31) {
          // Item sold - stop dramatic music and play sold sound

        }

        break;
      }
      case 'OnBlockCountdown': {
        const [LaneID, data] = payload;
        const { type, countdownFrom } = data || {};

        switch (type) {
          case '0': {
            this.virtualAuctionStore.resetCountdown(countdownFrom || 30);
            break
          }
          case '33': {
            // NO SALE
            // bidStore.playEffect('voices/wyatt/wyatt_no_sale.wav')
          }
        }

        if (type === 0) {
          // Start countdown and reset if already active
          this.virtualAuctionStore.resetCountdown(countdownFrom || 30); // Default to 30 seconds
        } else if (type === 1) {
          // Enter bonus time and reset if already active
          this.virtualAuctionStore.resetBonusTime(countdownFrom || 5); // Default to 5 seconds
        } else if (type === 2) {
          // End countdown
          this.virtualAuctionStore.stopCountdown();
        }
        break;
      }
      case 'ProviderEvents': {
        const [events] = payload;
        this.providerStore.setProviderEvents(events);
        break;
      }
      case 'ProviderSettings': {
        const [settings] = payload;
        this.providerStore.setProviderSettings(settings);
        this.sendMessage(key, { method: 'GetAuctionList' });
        // this.sendMessage(auctionID, { method: 'JoinAuction', payload: [auctionID]});
        break;
      }
      case 'RunList': {
        const [LaneID, data] = payload;
        bidStore.setRunList(LaneID, data);
        break;
      }
      case 'RunListComp': {
        const [LaneID, data] = payload;
        const decompressedData = JSON.parse(
            LZString.decompressFromEncodedURIComponent(data),
        );
        bidStore.setRunList(LaneID, decompressedData);
        break;
      }
      case 'SellerAction': {
        const [laneID, sellerActionInfo] = payload;
        const {
          sellerAccount,
          sellerAccountName,
          sellerAction,
          sellerActionAccount,
          sellerActionAccountName,
          sellerActionAmount,
          sellerActionMessage,
        } = sellerActionInfo;

        bidStore.mergeAuctionProperty(laneID, 'seller', {
          sellerAccount,
          sellerAccountName,
          sellerAction,
          sellerActionAccount,
          sellerActionAccountName,
          sellerActionAmount,
          sellerActionMessage,
        });

        break;
      }
      case 'SystemMessage': {
        const [data] = payload;

        const message = data?.message.toLowerCase();
        const { type } = data;

        if (type === -1) {
          if (message.indexOf('(force_reload)') > -1) {
            window.location.reload(true);
          }

          if (
              message.indexOf('(invalid_auction)') > -1 ||
              message.indexOf('(ip_blocked)') > -1 ||
              message.indexOf('(kicked_by_admin)') > -1
          ) {
            try {
              this.invalidAuctionError = true;
              this.connection
                  .stop()
                  .then(() => {
                    clearTimeout(this.networkCheckTimeout);

                    localStorage.clear();
                    this.connection = null;
                    window.location.href = 'https://www.npauctions.com';
                  })
                  .catch((error) => console.error('SystemMessage', error));
            }
            catch (error) {
              console.error('SystemMessage', error);
              localStorage.clear();
              this.connection = null;
              window.location.href = 'https://www.npauctions.com';
            }
          }
        }

        if (

            message.includes('login error:') &&
            !message.includes('session token is invalid')
        ) {
          localStorage.clear();
          window.reload();
        }

        if (data.type === -1) {
          const systemMessageLogger = new SimulcastLogger({
            component: 'DataStore',
            message: data?.message,
            messageType: 'error',
            method: 'emit > SystemMessage',
            params: {
              localStorage: window.localStorage,
              payload,
              stores: {
                auctionStore: auctionStore,
                authStore: this.authStore,
                bidStore: bidStore,
                dataStore: this,
                messageStore: messageStore,
                providerStore: this.providerStore,
              },
            },
            settings: LOGGER_SETTINGS,
            user: bidStore.user,
          });
          systemMessageLogger.logToConsole();
        }
        break;
      }
      case 'pong': {
        if (!this.multiAuctionStore.networkConnected) {
          this.multiAuctionStore.setProperty('networkConnected', true);
          if (this.reconnecting) {
            clearTimeout(this.reconnectTimeout);
            window.location.reload();
          }
        }

        if (this.networkCheckTimeout || this.invalidAuctionError) {
          clearTimeout(this.networkCheckTimeout);
          clearTimeout(this.reconnectTimeout)
        }
        break;
      }

      case 'BidAmountChanged': {
        bidStore.updateBlock(payload[0], payload[1]);
      }
        break;
      default:
        console.warn(`Unhandled message for auctionID: ${key}, event: ${eventName}`);
        break;
    }
  }
}


decorate(DataStore, {
  addNewConnection: action,
  closeConnection: action,
  connect: action,
  initialize: action,
  reconnecting: observable,
  reconnectionErrorMessage: observable,
  sendMessage: action,
});

export default DataStore;
